/**
 * PersonaManager - Central registry and stack manager for personas
 *
 * Responsibilities:
 * - Register and manage personas
 * - Maintain priority-ordered persona stack
 * - Handle exclusive persona behavior
 * - Coordinate view mode application
 * - Dispatch lifecycle events
 */

import { EventDispatcher } from '../EventDispatcher';
import { Viewer } from '../viewer/viewer';
import { IPersona, IPersonaConfig, PersonaEvents } from './types';
import { BasePersona } from './BasePersona';
import { ViewManager } from './ViewManager';
import { QuestRegistry, IQuestRegistry } from './quests/QuestRegistry';

/**
 * Persona Manager
 *
 * Central coordinator for the persona system.
 * Manages persona registration, activation, and stacking.
 *
 * @example
 * const manager = new PersonaManager(viewer);
 *
 * // Register personas
 * manager.registerPersona(AnnotationPersonaConfig);
 * manager.registerPersona(VolumePersonaConfig);
 *
 * // Enable a persona
 * manager.enablePersona('annotation');
 *
 * // Get active persona
 * const active = manager.getActivePersona();
 */
export class PersonaManager extends EventDispatcher {
  /** Viewer instance */
  private viewer: Viewer;

  /** Shared ViewManager for all personas */
  readonly viewManager: ViewManager;

  /** Quest registry for dynamic tooltip resolution */
  readonly questRegistry: IQuestRegistry = QuestRegistry;

  /** Registry of all personas (name → persona) */
  private personas: Map<string, IPersona> = new Map();

  /**
   * Active persona stack (sorted by priority, highest first)
   * Top of stack (index 0) is the highest priority active persona
   */
  private personaStack: IPersona[] = [];

  /** Debug mode flag */
  private debug = false;

  /**
   * Constructor
   *
   * @param viewer - Viewer instance
   */
  constructor(viewer: Viewer) {
    super();
    this.viewer = viewer;

    // Create shared ViewManager
    this.viewManager = new ViewManager(viewer, 'PersonaManager');

    // Enable debug logging via environment or localStorage
    if (typeof window !== 'undefined') {
      this.debug = localStorage.getItem('persona_debug') === 'true';
    }
  }

  // ==================== Persona Registration ====================

  /**
   * Register a new persona
   *
   * Creates persona instance and adds to registry.
   * Does not enable the persona.
   *
   * @param config - Persona configuration
   * @returns Created persona instance
   * @throws Error if persona name already registered
   *
   * @example
   * const persona = manager.registerPersona({
   *   name: 'annotation',
   *   priority: 10,
   *   exclusive: true,
   *   tools: [measurePointConfig],
   * });
   */
  registerPersona(config: IPersonaConfig): IPersona {
    if (this.personas.has(config.name)) {
      throw new Error(`Persona "${config.name}" already registered`);
    }

    // Create persona instance using factory if provided, otherwise use BasePersona
    // Pass the shared viewManager to all personas
    const persona = config.factory ? config.factory(this.viewer, config, this.viewManager) : new BasePersona(this.viewer, config, this.viewManager);

    // Register
    this.personas.set(config.name, persona);

    // Listen to persona events
    persona.addEventListener(PersonaEvents.ENABLED, this.handlePersonaEnabled.bind(this));
    persona.addEventListener(PersonaEvents.DISABLED, this.handlePersonaDisabled.bind(this));

    return persona;
  }

  /**
   * Unregister a persona
   *
   * Disables and removes persona from registry.
   *
   * @param name - Persona identifier
   */
  unregisterPersona(name: string): void {
    const persona = this.personas.get(name);
    if (!persona) {
      this.warn(`Persona "${name}" not found`);
      return;
    }

    // Disable if enabled
    if (persona.enabled) {
      persona.disable();
    }

    // Dispose
    persona.dispose();

    // Unregister
    this.personas.delete(name);
  }

  /**
   * Get a registered persona
   *
   * @param name - Persona identifier
   * @returns Persona instance or undefined
   */
  getPersona(name: string): IPersona | undefined {
    return this.personas.get(name);
  }

  /**
   * Get all registered personas
   *
   * @returns Array of all personas
   */
  getAllPersonas(): IPersona[] {
    return Array.from(this.personas.values());
  }

  // ==================== Persona Activation ====================

  /**
   * Enable a persona
   *
   * Adds persona to stack and sorts by priority.
   * If persona is exclusive, disables all other personas first.
   *
   * @param name - Persona identifier
   * @throws Error if persona not found
   *
   * @example
   * manager.enablePersona('annotation');
   */
  enablePersona(name: string): void {
    const persona = this.personas.get(name);
    if (!persona) {
      throw new Error(`Persona "${name}" not found`);
    }

    // Check if already enabled
    if (persona.enabled) {
      this.warn(`Persona "${name}" already enabled`);
      return;
    }

    // Handle exclusive personas
    if (persona.config.exclusive) {
      this.disableAllPersonas();
    }

    // Enable persona (will trigger ENABLED event)
    persona.enable();
  }

  /**
   * Disable a persona
   *
   * Removes persona from stack.
   *
   * @param name - Persona identifier
   */
  disablePersona(name: string): void {
    const persona = this.personas.get(name);
    if (!persona) {
      this.warn(`Persona "${name}" not found`);
      return;
    }

    if (!persona.enabled) {
      this.warn(`Persona "${name}" already disabled`);
      return;
    }

    // Disable persona (will trigger DISABLED event)
    persona.disable();
  }

  /**
   * Disable all personas
   *
   * Clears the persona stack.
   */
  disableAllPersonas(): void {
    // Create copy of stack to avoid modification during iteration
    const stack = [...this.personaStack];

    for (const persona of stack) {
      persona.disable();
    }
  }

  /**
   * Toggle a persona
   *
   * Enables if disabled, disables if enabled.
   *
   * @param name - Persona identifier
   * @returns New enabled state
   */
  togglePersona(name: string): boolean {
    const persona = this.personas.get(name);
    if (!persona) {
      throw new Error(`Persona "${name}" not found`);
    }

    if (persona.enabled) {
      this.disablePersona(name);
      return false;
    } else {
      this.enablePersona(name);
      return true;
    }
  }

  // ==================== Persona Stack ====================

  /**
   * Get the active persona stack
   *
   * Sorted by priority (highest first).
   *
   * @returns Array of active personas
   */
  getActivePersonas(): IPersona[] {
    return [...this.personaStack];
  }

  /**
   * Get the top-priority active persona
   *
   * This is the persona that handles input events first.
   *
   * @returns Top persona or undefined if none active
   */
  getActivePersona(): IPersona | undefined {
    return this.personaStack[0];
  }

  /**
   * Check if a persona is active
   *
   * @param name - Persona identifier
   * @returns True if persona is in active stack
   */
  isPersonaActive(name: string): boolean {
    const persona = this.personas.get(name);
    return persona?.enabled ?? false;
  }

  /**
   * Get count of active personas
   *
   * @returns Number of active personas
   */
  getActivePersonaCount(): number {
    return this.personaStack.length;
  }

  // ==================== Event Handlers ====================

  /**
   * Handle persona enabled event
   *
   * - Adds to stack
   * - Sorts by priority
   * - Applies view from top persona
   */
  private handlePersonaEnabled(event: { type: string; persona?: unknown } & Record<string, unknown>): void {
    const persona = event.persona as IPersona;

    // Add to stack if not already there
    if (!this.personaStack.includes(persona)) {
      this.personaStack.push(persona);
      this.sortPersonaStack();
    }

    // Apply view from top persona
    const topPersona = this.personaStack[0];
    if (topPersona) {
      // View is already applied by persona.enable(), no need to reapply
    }

    // Forward event
    this.dispatchEvent({
      type: 'persona_manager_stack_changed',
      manager: this,
      persona,
      action: 'enabled',
    });
  }

  /**
   * Handle persona disabled event
   *
   * - Removes from stack
   * - Applies view from new top persona
   */
  private handlePersonaDisabled(event: { type: string; persona?: unknown } & Record<string, unknown>): void {
    const persona = event.persona as IPersona;

    // Remove from stack
    const index = this.personaStack.indexOf(persona);
    if (index !== -1) {
      this.personaStack.splice(index, 1);
    }

    // Apply view from new top persona
    const topPersona = this.personaStack[0];
    if (topPersona) {
      const currentView = topPersona.getCurrentView();
      if (currentView) {
        topPersona.setView(currentView.id);
      }
    }

    // Forward event
    this.dispatchEvent({
      type: 'persona_manager_stack_changed',
      manager: this,
      persona,
      action: 'disabled',
    });
  }

  /**
   * Sort persona stack by priority (descending)
   *
   * Higher priority personas are first in the array.
   */
  private sortPersonaStack(): void {
    this.personaStack.sort((a, b) => b.priority - a.priority);
  }

  // ==================== Cleanup ====================

  /**
   * Cleanup and dispose all personas
   *
   * - Unregisters all personas
   * - Clears stack
   * - Removes event listeners
   */
  dispose(): void {
    // Unregister all personas
    const personaNames = Array.from(this.personas.keys());
    for (const name of personaNames) {
      this.unregisterPersona(name);
    }

    this.personas.clear();
    this.personaStack = [];
  }

  // ==================== Utilities ====================

  /**
   * Get debug info about current state
   *
   * @returns Debug information object
   */
  getDebugInfo(): Record<string, unknown> {
    return {
      registeredPersonas: Array.from(this.personas.keys()),
      activePersonas: this.personaStack.map((p) => ({
        name: p.name,
        priority: p.priority,
        enabledTools: p.getEnabledTools().map((t) => t.name),
        currentView: p.getCurrentView()?.id,
      })),
      topPersona: this.personaStack[0]?.name,
    };
  }

  /**
   * Enable debug logging
   */
  enableDebug(): void {
    this.debug = true;
    if (typeof window !== 'undefined') {
      localStorage.setItem('persona_debug', 'true');
    }
  }

  /**
   * Disable debug logging
   */
  disableDebug(): void {
    this.debug = false;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('persona_debug');
    }
  }

  // ==================== Helper Methods ====================

  /**
   * Helper: Log debug message
   */
  private log(message: string, ...args: unknown[]): void {
    if (this.debug) {
      console.log(`[PersonaManager] ${message}`, ...args);
    }
  }

  /**
   * Helper: Log warning message
   */
  private warn(message: string, ...args: unknown[]): void {
    console.warn(`[PersonaManager] ${message}`, ...args);
  }

  /**
   * Helper: Log error message
   */
  private error(message: string, ...args: unknown[]): void {
    console.error(`[PersonaManager] ${message}`, ...args);
  }
}
